import { PayloadAction, SliceCaseReducers, createSelector, createSlice } from '@reduxjs/toolkit';
import { DataTypePillType } from 'app/components/CommonStyled/DataTypePill/DataTypePill';
import { ResourcesSection } from 'app/components/Resources';
import { IResource } from 'app/components/Resources/ResourceCard';
import { ICollectionDetail, IResourceUuid } from 'app/components/Resources/interfaces';
import { newSelectedCriteriaToOldModels } from 'utils/constants';

export const NO_COMPANY_COLLECTION_DETAIL: ICollectionDetail = {
  uuid: null,
  name: 'No company assigned',
  slug: null,
};

export const NO_INDUSTRY_COLLECTION_DETAIL: ICollectionDetail = {
  uuid: null,
  name: 'No industry assigned',
  slug: null,
};

export const NO_CLIENT_COLLECTION_DETAIL: ICollectionDetail = {
  uuid: null,
  name: 'No client assigned',
  slug: null,
};

export const RESOURCES_FILTER_TYPE = {
  LEVELS: 'levels',
  CURRENT_COMPANY: 'companies',
  PREVIOUSLY_WORKED_AT: 'prior_companies',
  INDUSTRIES: 'industries',
  SKILLS: 'skills',
  DISCIPLINES: 'disciplines',
  ROLES: 'roles',
  CLIENTS: 'clients',
  PASSIONS: 'passions',
  COMMUNITIES: 'communities',
  LOCATIONS: 'locations',
  LANGUAGES: 'languages',
  TENURE: 'tenure',
  WORK_EXPERIENCE: 'work_experience',
  PROJECT_LEADS: 'project_leads',
  CONTRIBUTED_RESOURCES: 'contributed_resources',
  HOSTED_EVENTS: 'hosted_events',
  OTHER_COMPANIES: 'other_companies',
  ONLY_COLLEAGUES: 'my_company',
  EXCLUDE_COLLEAGUES: 'other_companies',
  INCLUDE_FREELANCERS: 'company_freelancers',
  AVAILABLE_CROSS_KYU_TEAMING: 'cross_kyu_teaming',
  GROUPS: 'groups',
  RESOURCE_TYPES: 'resource_types',
  FORMATS: 'formats',
  RESOURCES: 'resources',
  TAGS: 'tags',
} as const;

export type IResourcesFilterType =
  (typeof RESOURCES_FILTER_TYPE)[keyof typeof RESOURCES_FILTER_TYPE];

export type IResourcesExclusiveFilterType =
  | typeof RESOURCES_FILTER_TYPE.PROJECT_LEADS
  | typeof RESOURCES_FILTER_TYPE.CONTRIBUTED_RESOURCES
  | typeof RESOURCES_FILTER_TYPE.HOSTED_EVENTS
  | typeof RESOURCES_FILTER_TYPE.OTHER_COMPANIES
  | typeof RESOURCES_FILTER_TYPE.ONLY_COLLEAGUES
  | typeof RESOURCES_FILTER_TYPE.EXCLUDE_COLLEAGUES
  | typeof RESOURCES_FILTER_TYPE.INCLUDE_FREELANCERS
  | typeof RESOURCES_FILTER_TYPE.AVAILABLE_CROSS_KYU_TEAMING;

export type IResourcesSortType = 'relevance' | 'name';

export interface IResourcesFilter {
  name?: string;
  uuid: string;
  original_name: string;
  aliases: string[];
  grouping: string;
}

export interface IResourcesFilters {
  [RESOURCES_FILTER_TYPE.LEVELS]?: IResourcesFilter[];
  [RESOURCES_FILTER_TYPE.CURRENT_COMPANY]?: IResourcesFilter[];
  [RESOURCES_FILTER_TYPE.PREVIOUSLY_WORKED_AT]?: IResourcesFilter[];
  [RESOURCES_FILTER_TYPE.INDUSTRIES]?: IResourcesFilter[];
  [RESOURCES_FILTER_TYPE.SKILLS]?: IResourcesFilter[];
  [RESOURCES_FILTER_TYPE.DISCIPLINES]?: IResourcesFilter[];
  [RESOURCES_FILTER_TYPE.ROLES]?: IResourcesFilter[];
  [RESOURCES_FILTER_TYPE.CLIENTS]?: IResourcesFilter[];
  [RESOURCES_FILTER_TYPE.PASSIONS]?: IResourcesFilter[];
  [RESOURCES_FILTER_TYPE.COMMUNITIES]?: IResourcesFilter[];
  [RESOURCES_FILTER_TYPE.LOCATIONS]?: IResourcesFilter[];
  [RESOURCES_FILTER_TYPE.LANGUAGES]?: IResourcesFilter[];
  [RESOURCES_FILTER_TYPE.TENURE]?: IResourcesFilter[];
  [RESOURCES_FILTER_TYPE.WORK_EXPERIENCE]?: IResourcesFilter[];
  [RESOURCES_FILTER_TYPE.RESOURCE_TYPES]?: IResourcesFilter[];
  [RESOURCES_FILTER_TYPE.FORMATS]?: IResourcesFilter[];
  [RESOURCES_FILTER_TYPE.RESOURCES]?: IResourcesFilter[];
  [RESOURCES_FILTER_TYPE.TAGS]?: IResourcesFilter[];
}

export interface IResourcesDetails {
  company_freelancers: number;
  cross_kyu_teaming: number;
  my_company: number;
}

export interface IResourcesOnboarding {
  client: {
    uuid: string;
    name: string;
  };
  skill: {
    uuid: string;
    name: string;
  };
  industry: {
    uuid: string;
    name: string;
  };
  discipline: {
    uuid: string;
    name: string;
  };
  passion: {
    uuid: string;
    name: string;
  };
  company: {
    uuid: string;
    name: string;
  };
}

export interface IResourcesUuids extends IResourceUuid {}

export interface IResourcesNLP {
  uuid: string;
  name: string;
  slug: string;
  groups_matched: string[];
  engagement: IResourcesUuidsEngagement;
  level: IResourcesUuidsLevel;
  location: IResourcesUuidsLocation;
}

export interface IResourcesUuidsEngagement {
  cross_kyu_teaming: boolean;
  type: string;
  company: IResourcesUuidsCompany;
}

export interface IResourcesUuidsCompany {
  uuid: string;
  name: string;
}

export interface IResourcesUuidsLevel {
  uuid: string;
  name: string;
}

export interface IResourcesUuidsLocation {
  uuid: string;
  name: string;
  country: string | null;
}

export interface IResourcesExclusiveFilters {
  [RESOURCES_FILTER_TYPE.PROJECT_LEADS]?: boolean;
  [RESOURCES_FILTER_TYPE.CONTRIBUTED_RESOURCES]?: boolean;
  [RESOURCES_FILTER_TYPE.HOSTED_EVENTS]?: boolean;
  [RESOURCES_FILTER_TYPE.OTHER_COMPANIES]?: boolean;
  [RESOURCES_FILTER_TYPE.ONLY_COLLEAGUES]?: boolean;
  [RESOURCES_FILTER_TYPE.EXCLUDE_COLLEAGUES]?: boolean;
  [RESOURCES_FILTER_TYPE.INCLUDE_FREELANCERS]?: boolean;
  [RESOURCES_FILTER_TYPE.AVAILABLE_CROSS_KYU_TEAMING]?: boolean;
}

export const RESULTS_BANNER_FILTERS = {
  MUST_INCLUDE: 'must_include',
  COMPANY: 'company',
  LEVEL: 'level',
  LOCATION: 'location',
} as const;

export type IResultsBannerFilterType =
  (typeof RESULTS_BANNER_FILTERS)[keyof typeof RESULTS_BANNER_FILTERS];

export interface ResourcesState {
  details: IResourcesDetails;
  filters: IResourcesFilters;
  filters_length: number;
  previous_filters: IResourcesFilters;
  previous_filters_length: number;
  exclusive_filters: IResourcesExclusiveFilters;
  resources: IResource[];
  original_resources: IResource[];
  default_resources: IResource[];
  selected_resources: IResource[];
  uuids: IResourcesUuids[];
  default_uuids: IResourcesUuids[];
  should_fetch: boolean;
  current_page: number;
  should_get_more_resources: boolean;
  is_resources_loading: boolean;
  is_more_resources_loading: boolean;
  force_dropdown_filter_open: boolean;
  current_selected_person: IResource | null;
  show_contact_drawer: boolean;
  resources_error: boolean;
  sort: IResourcesSortType;
  open_mobile_nav: boolean;
  current_filter_submenu_open: IResourcesFilterType | null;
  show_beacons: boolean;
  current_beacon_step: number;
  show_onboarding_modal: boolean;
  force_scroll_sidebar: boolean;
  should_show_beacons: boolean;
  show_teaming_contact_drawer: boolean;
  selected_criteria: {
    [key in DataTypePillType]?: string[];
  };
  force_skeleton_list: boolean;
  selection_filters: {
    [RESOURCES_FILTER_TYPE.GROUPS]?: string[];
  } & {
    [key in Exclude<
      IResourcesFilterType,
      typeof RESOURCES_FILTER_TYPE.GROUPS
    >]?: IResourcesFilter[];
  };
  optional_filters: {
    [key in IResourcesFilterType]?: IResourcesFilter[];
  };
  additional_optional_filters: {
    employees: boolean;
    freelancers: boolean;
  };
  results_banner_filters: {
    currently_showing: IResultsBannerFilterType[];
  };
  search_bar: {
    current_tab: string;
  };
  current_step: number;
  nlp_resources_results: IResourcesNLP[];
  scope: {
    type: string | null;
    uuid: string | null;
  };
  show_back_button: boolean;
  current_section: ResourcesSection;
  drafts: IResource[];
  scroll_position: number;
}

const exclusiveFilterToTitle = new Map<IResourcesFilterType, string>([
  [RESOURCES_FILTER_TYPE.PROJECT_LEADS, `they're project leads`],
  [RESOURCES_FILTER_TYPE.OTHER_COMPANIES, `they're not from your company`],
  [RESOURCES_FILTER_TYPE.CONTRIBUTED_RESOURCES, `they contributed resources`],
  [RESOURCES_FILTER_TYPE.HOSTED_EVENTS, `they hosted events`],
  [RESOURCES_FILTER_TYPE.ONLY_COLLEAGUES, `they're your colleagues`],
  [RESOURCES_FILTER_TYPE.INCLUDE_FREELANCERS, `they're freelancers`],
]);

const getFiltersLength = (
  filters: IResourcesFilters,
  exclusive_filters: IResourcesExclusiveFilters
): number => {
  let allInclusiveFilters = 0;
  let allExclusiveFilters = 0;

  if (filters) {
    allInclusiveFilters = Object.values(filters)?.reduce((acc, filter) => {
      if (!filter) return acc;
      if (filter === true) return acc + 1;
      return acc + filter?.length;
    }, 0);
  }

  if (exclusive_filters) {
    allExclusiveFilters = Object.values(exclusive_filters)?.reduce(
      (acc, filter) => (filter ? acc + 1 : acc),
      0
    );
  }

  return allInclusiveFilters + allExclusiveFilters;
};

export const slice = createSlice<ResourcesState, SliceCaseReducers<ResourcesState>>({
  name: 'resources',
  initialState: {
    details: {
      company_freelancers: 0,
      cross_kyu_teaming: 0,
      my_company: 0,
    },
    filters: {
      [RESOURCES_FILTER_TYPE.LEVELS]: [],
      [RESOURCES_FILTER_TYPE.CURRENT_COMPANY]: [],
      [RESOURCES_FILTER_TYPE.PREVIOUSLY_WORKED_AT]: [],
      [RESOURCES_FILTER_TYPE.INDUSTRIES]: [],
      [RESOURCES_FILTER_TYPE.SKILLS]: [],
      [RESOURCES_FILTER_TYPE.DISCIPLINES]: [],
      [RESOURCES_FILTER_TYPE.ROLES]: [],
      [RESOURCES_FILTER_TYPE.CLIENTS]: [],
      [RESOURCES_FILTER_TYPE.PASSIONS]: [],
      [RESOURCES_FILTER_TYPE.COMMUNITIES]: [],
      [RESOURCES_FILTER_TYPE.LOCATIONS]: [],
      [RESOURCES_FILTER_TYPE.LANGUAGES]: [],
      [RESOURCES_FILTER_TYPE.TENURE]: [],
      [RESOURCES_FILTER_TYPE.WORK_EXPERIENCE]: [],
      [RESOURCES_FILTER_TYPE.RESOURCE_TYPES]: [],
      [RESOURCES_FILTER_TYPE.FORMATS]: [],
      [RESOURCES_FILTER_TYPE.RESOURCES]: [],
      [RESOURCES_FILTER_TYPE.TAGS]: [],
    },
    filters_length: 0,
    previous_filters: {
      [RESOURCES_FILTER_TYPE.LEVELS]: [],
      [RESOURCES_FILTER_TYPE.CURRENT_COMPANY]: [],
      [RESOURCES_FILTER_TYPE.PREVIOUSLY_WORKED_AT]: [],
      [RESOURCES_FILTER_TYPE.INDUSTRIES]: [],
      [RESOURCES_FILTER_TYPE.SKILLS]: [],
      [RESOURCES_FILTER_TYPE.DISCIPLINES]: [],
      [RESOURCES_FILTER_TYPE.ROLES]: [],
      [RESOURCES_FILTER_TYPE.CLIENTS]: [],
      [RESOURCES_FILTER_TYPE.PASSIONS]: [],
      [RESOURCES_FILTER_TYPE.COMMUNITIES]: [],
      [RESOURCES_FILTER_TYPE.LOCATIONS]: [],
      [RESOURCES_FILTER_TYPE.LANGUAGES]: [],
      [RESOURCES_FILTER_TYPE.TENURE]: [],
      [RESOURCES_FILTER_TYPE.WORK_EXPERIENCE]: [],
      [RESOURCES_FILTER_TYPE.RESOURCE_TYPES]: [],
      [RESOURCES_FILTER_TYPE.FORMATS]: [],
      [RESOURCES_FILTER_TYPE.RESOURCES]: [],
      [RESOURCES_FILTER_TYPE.TAGS]: [],
    },
    exclusive_filters: {
      [RESOURCES_FILTER_TYPE.PROJECT_LEADS]: false,
      [RESOURCES_FILTER_TYPE.CONTRIBUTED_RESOURCES]: false,
      [RESOURCES_FILTER_TYPE.HOSTED_EVENTS]: false,
      [RESOURCES_FILTER_TYPE.OTHER_COMPANIES]: false,
      [RESOURCES_FILTER_TYPE.ONLY_COLLEAGUES]: false,
      [RESOURCES_FILTER_TYPE.INCLUDE_FREELANCERS]: false,
      [RESOURCES_FILTER_TYPE.AVAILABLE_CROSS_KYU_TEAMING]: false,
    },
    previous_filters_length: 0,
    resources: [],
    original_resources: [],
    default_resources: [],
    selected_resources: [],
    uuids: [],
    default_uuids: [],
    should_fetch: false,
    current_page: 1,
    should_get_more_resources: false,
    is_resources_loading: false,
    is_more_resources_loading: false,
    force_dropdown_filter_open: false,
    current_selected_person: null,
    show_contact_drawer: false,
    resources_error: false,
    sort: 'relevance',
    open_mobile_nav: false,
    current_filter_submenu_open: null,
    show_beacons: false,
    current_beacon_step: 1,
    show_onboarding_modal: false,
    force_scroll_sidebar: false,
    should_show_beacons: false,
    show_teaming_contact_drawer: false,
    selected_criteria: null,
    force_skeleton_list: false,
    selection_filters: null,
    optional_filters: {
      [RESOURCES_FILTER_TYPE.CURRENT_COMPANY]: [],
      [RESOURCES_FILTER_TYPE.LOCATIONS]: [],
      [RESOURCES_FILTER_TYPE.LEVELS]: [],
      [RESOURCES_FILTER_TYPE.INDUSTRIES]: [],
      [RESOURCES_FILTER_TYPE.CLIENTS]: [],
      [RESOURCES_FILTER_TYPE.FORMATS]: [],
      [RESOURCES_FILTER_TYPE.RESOURCE_TYPES]: [],
    },
    additional_optional_filters: {
      employees: false,
      freelancers: false,
    },
    results_banner_filters: {
      currently_showing: [],
    },
    current_step: 1,
    search_bar: {
      current_tab: 'exact_matches',
    },
    nlp_resources_results: [],
    scope: {
      type: null,
      uuid: null,
    },
    show_back_button: false,
    current_section: ResourcesSection.RESOURCES,
    drafts: [],
    scroll_position: 0,
  },
  reducers: {
    setDetails: (state, action: PayloadAction<IResourcesDetails>) => {
      state.details = action.payload;
    },
    addFilter: (
      state,
      action: { payload: { type: IResourcesFilterType; filters: IResourcesFilter[] } }
    ) => {
      if (!state.filters[action.payload.type]) {
        state.filters[action.payload.type] = [];
      }

      state.filters[action.payload.type] = [
        ...state.filters[action.payload.type],
        ...action.payload.filters,
      ];

      state.filters_length = getFiltersLength(state.filters, state.exclusive_filters);
    },
    setExclusiveFilter: (
      state,
      action: { payload: { type: IResourcesExclusiveFilterType; state: boolean } }
    ) => {
      state.exclusive_filters[action.payload.type] = action.payload.state;
      state.filters_length = getFiltersLength(state.filters, state.exclusive_filters);
    },
    setExclusiveFilters: (state, action: { payload: { filters: IResourcesExclusiveFilters } }) => {
      state.exclusive_filters = action.payload.filters;
      state.filters_length = getFiltersLength(state.filters, state.exclusive_filters);
    },
    setFilter: (
      state,
      action: { payload: { type: IResourcesFilterType; filters: IResourcesFilter[] } }
    ) => {
      state.filters[action.payload.type] = action.payload.filters;
      state.filters_length = getFiltersLength(state.filters, state.exclusive_filters);
    },
    setFilters: (
      state,
      action: {
        payload: {
          filters: IResourcesFilters;
          exclusive_filters: IResourcesExclusiveFilters;
        };
      }
    ) => {
      state.filters = {
        [RESOURCES_FILTER_TYPE.LEVELS]: action.payload.filters[RESOURCES_FILTER_TYPE.LEVELS] || [],
        [RESOURCES_FILTER_TYPE.CURRENT_COMPANY]:
          action.payload.filters[RESOURCES_FILTER_TYPE.CURRENT_COMPANY] || [],
        [RESOURCES_FILTER_TYPE.PREVIOUSLY_WORKED_AT]:
          action.payload.filters[RESOURCES_FILTER_TYPE.PREVIOUSLY_WORKED_AT] || [],
        [RESOURCES_FILTER_TYPE.INDUSTRIES]:
          action.payload.filters[RESOURCES_FILTER_TYPE.INDUSTRIES] || [],
        [RESOURCES_FILTER_TYPE.SKILLS]: action.payload.filters[RESOURCES_FILTER_TYPE.SKILLS] || [],
        [RESOURCES_FILTER_TYPE.DISCIPLINES]:
          action.payload.filters[RESOURCES_FILTER_TYPE.DISCIPLINES] || [],
        [RESOURCES_FILTER_TYPE.ROLES]: action.payload.filters[RESOURCES_FILTER_TYPE.ROLES] || [],
        [RESOURCES_FILTER_TYPE.CLIENTS]:
          action.payload.filters[RESOURCES_FILTER_TYPE.CLIENTS] || [],
        [RESOURCES_FILTER_TYPE.PASSIONS]:
          action.payload.filters[RESOURCES_FILTER_TYPE.PASSIONS] || [],
        [RESOURCES_FILTER_TYPE.COMMUNITIES]:
          action.payload.filters[RESOURCES_FILTER_TYPE.COMMUNITIES] || [],
        [RESOURCES_FILTER_TYPE.LOCATIONS]:
          action.payload.filters[RESOURCES_FILTER_TYPE.LOCATIONS] || [],
        [RESOURCES_FILTER_TYPE.LANGUAGES]:
          action.payload.filters[RESOURCES_FILTER_TYPE.LANGUAGES] || [],
        [RESOURCES_FILTER_TYPE.TENURE]: action.payload.filters[RESOURCES_FILTER_TYPE.TENURE] || [],
        [RESOURCES_FILTER_TYPE.WORK_EXPERIENCE]:
          action.payload.filters[RESOURCES_FILTER_TYPE.WORK_EXPERIENCE] || [],
        [RESOURCES_FILTER_TYPE.RESOURCE_TYPES]:
          action.payload.filters[RESOURCES_FILTER_TYPE.RESOURCE_TYPES] || [],
        [RESOURCES_FILTER_TYPE.FORMATS]:
          action.payload.filters[RESOURCES_FILTER_TYPE.FORMATS] || [],
        [RESOURCES_FILTER_TYPE.RESOURCES]:
          action.payload.filters[RESOURCES_FILTER_TYPE.RESOURCES] || [],
        [RESOURCES_FILTER_TYPE.TAGS]: action.payload.filters[RESOURCES_FILTER_TYPE.TAGS] || [],
      };
      state.exclusive_filters = {
        [RESOURCES_FILTER_TYPE.PROJECT_LEADS]:
          action.payload.exclusive_filters?.[RESOURCES_FILTER_TYPE.PROJECT_LEADS] || false,
        [RESOURCES_FILTER_TYPE.CONTRIBUTED_RESOURCES]:
          action.payload.exclusive_filters?.[RESOURCES_FILTER_TYPE.CONTRIBUTED_RESOURCES] || false,
        [RESOURCES_FILTER_TYPE.HOSTED_EVENTS]:
          action.payload.exclusive_filters?.[RESOURCES_FILTER_TYPE.HOSTED_EVENTS] || false,
        [RESOURCES_FILTER_TYPE.OTHER_COMPANIES]:
          action.payload.exclusive_filters?.[RESOURCES_FILTER_TYPE.OTHER_COMPANIES] || false,
        [RESOURCES_FILTER_TYPE.ONLY_COLLEAGUES]:
          action.payload.exclusive_filters?.[RESOURCES_FILTER_TYPE.ONLY_COLLEAGUES] || false,
        [RESOURCES_FILTER_TYPE.INCLUDE_FREELANCERS]:
          action.payload.exclusive_filters?.[RESOURCES_FILTER_TYPE.INCLUDE_FREELANCERS] || false,
        [RESOURCES_FILTER_TYPE.AVAILABLE_CROSS_KYU_TEAMING]:
          action.payload.exclusive_filters?.[RESOURCES_FILTER_TYPE.AVAILABLE_CROSS_KYU_TEAMING] ||
          false,
      };
      state.filters_length = getFiltersLength(state.filters, state.exclusive_filters);
    },
    removeFromFilter: (
      state,
      action: { payload: { type: IResourcesFilterType; filters: IResourcesFilter[] } }
    ) => {
      const filtersUuids = action.payload.filters.map((f) => f.uuid);

      state.filters[action.payload.type] = state.filters[action.payload.type].filter(
        (filter) => !filtersUuids.includes(filter.uuid)
      );

      state.filters_length = getFiltersLength(state.filters, state.exclusive_filters);
    },
    emptyFilter: (state, action: { payload: IResourcesFilterType }) => {
      state.filters[action.payload] = [];
      state.filters_length = getFiltersLength(state.filters, state.exclusive_filters);
    },
    emptyAllFilters: (state) => {
      state.filters = {
        [RESOURCES_FILTER_TYPE.LEVELS]: [],
        [RESOURCES_FILTER_TYPE.CURRENT_COMPANY]: [],
        [RESOURCES_FILTER_TYPE.PREVIOUSLY_WORKED_AT]: [],
        [RESOURCES_FILTER_TYPE.INDUSTRIES]: [],
        [RESOURCES_FILTER_TYPE.SKILLS]: [],
        [RESOURCES_FILTER_TYPE.DISCIPLINES]: [],
        [RESOURCES_FILTER_TYPE.ROLES]: [],
        [RESOURCES_FILTER_TYPE.CLIENTS]: [],
        [RESOURCES_FILTER_TYPE.PASSIONS]: [],
        [RESOURCES_FILTER_TYPE.COMMUNITIES]: [],
        [RESOURCES_FILTER_TYPE.LOCATIONS]: [],
        [RESOURCES_FILTER_TYPE.LANGUAGES]: [],
        [RESOURCES_FILTER_TYPE.TENURE]: [],
        [RESOURCES_FILTER_TYPE.WORK_EXPERIENCE]: [],
        [RESOURCES_FILTER_TYPE.RESOURCE_TYPES]: [],
        [RESOURCES_FILTER_TYPE.FORMATS]: [],
        [RESOURCES_FILTER_TYPE.RESOURCES]: [],
        [RESOURCES_FILTER_TYPE.TAGS]: [],
      };
      state.previous_filters = {
        [RESOURCES_FILTER_TYPE.LEVELS]: [],
        [RESOURCES_FILTER_TYPE.CURRENT_COMPANY]: [],
        [RESOURCES_FILTER_TYPE.PREVIOUSLY_WORKED_AT]: [],
        [RESOURCES_FILTER_TYPE.INDUSTRIES]: [],
        [RESOURCES_FILTER_TYPE.SKILLS]: [],
        [RESOURCES_FILTER_TYPE.DISCIPLINES]: [],
        [RESOURCES_FILTER_TYPE.ROLES]: [],
        [RESOURCES_FILTER_TYPE.CLIENTS]: [],
        [RESOURCES_FILTER_TYPE.PASSIONS]: [],
        [RESOURCES_FILTER_TYPE.COMMUNITIES]: [],
        [RESOURCES_FILTER_TYPE.LOCATIONS]: [],
        [RESOURCES_FILTER_TYPE.LANGUAGES]: [],
        [RESOURCES_FILTER_TYPE.TENURE]: [],
        [RESOURCES_FILTER_TYPE.WORK_EXPERIENCE]: [],
        [RESOURCES_FILTER_TYPE.RESOURCE_TYPES]: [],
        [RESOURCES_FILTER_TYPE.FORMATS]: [],
        [RESOURCES_FILTER_TYPE.RESOURCES]: [],
        [RESOURCES_FILTER_TYPE.TAGS]: [],
      };
      state.exclusive_filters = {
        [RESOURCES_FILTER_TYPE.PROJECT_LEADS]: false,
        [RESOURCES_FILTER_TYPE.CONTRIBUTED_RESOURCES]: false,
        [RESOURCES_FILTER_TYPE.HOSTED_EVENTS]: false,
        [RESOURCES_FILTER_TYPE.OTHER_COMPANIES]: false,
      };
      state.selection_filters = null;
      state.optional_filters = {
        [RESOURCES_FILTER_TYPE.CURRENT_COMPANY]: [],
        [RESOURCES_FILTER_TYPE.LOCATIONS]: [],
        [RESOURCES_FILTER_TYPE.LEVELS]: [],
        [RESOURCES_FILTER_TYPE.RESOURCE_TYPES]: [],
        [RESOURCES_FILTER_TYPE.FORMATS]: [],
        [RESOURCES_FILTER_TYPE.CLIENTS]: [],
        [RESOURCES_FILTER_TYPE.INDUSTRIES]: [],
      };
      state.results_banner_filters.currently_showing = [];
      state.filters_length = 0;
      state.previous_filters_length = 0;
    },
    updatePreviousFilters: (state, action: PayloadAction<null>) => {
      state.previous_filters = { ...state.filters };
      state.previous_filters_length = state.filters_length;
    },
    setResources: (state, action: PayloadAction<IResource[]>) => {
      state.resources = action.payload;
    },
    setOriginalResources: (state, action: PayloadAction<IResource[]>) => {
      state.original_resources = action.payload;
    },
    setDefaultResources: (state, action: PayloadAction<IResource[]>) => {
      state.default_resources = action.payload;
    },
    pushResources: (state, action: PayloadAction<IResource[]>) => {
      state.resources = [...state.resources, ...action.payload];
    },
    setSelectedResources: (state, action: PayloadAction<IResource[]>) => {
      state.selected_resources = action.payload;
    },
    pushSelectedResources: (state, action: PayloadAction<IResource[]>) => {
      state.selected_resources = [...state.selected_resources, ...action.payload];
    },
    removeSelectedResources: (state, action: PayloadAction<IResource[]>) => {
      state.selected_resources = state.selected_resources.filter(
        (person) => !action.payload.some((p) => p.uuid === person.uuid)
      );
    },
    setUuids: (state, action: PayloadAction<IResourcesUuids[]>) => {
      state.uuids = action.payload;
    },
    setDefaultUuids: (state, action: PayloadAction<IResourcesUuids[]>) => {
      state.default_uuids = action.payload;
    },
    setShouldFetch: (state, action: PayloadAction<boolean>) => {
      state.open_mobile_nav = false;
      state.current_page = 1;
      state.should_fetch = action.payload;
    },
    setShouldGetMoreResources: (state, action: PayloadAction<boolean>) => {
      state.should_get_more_resources = action.payload;
    },
    setCurrentPage: (state, action: PayloadAction<number>) => {
      state.current_page = action.payload;
    },
    setIsResourcesLoading: (state, action: PayloadAction<boolean>) => {
      state.is_resources_loading = action.payload;
    },
    setIsMoreResourcesLoading: (state, action: PayloadAction<boolean>) => {
      state.is_more_resources_loading = action.payload;
    },
    setForceDropdownFilterOpen: (state, action: PayloadAction<boolean>) => {
      state.force_dropdown_filter_open = action.payload;
    },
    setCurrentSelectedPerson: (state, action: PayloadAction<IResource | null>) => {
      state.current_selected_person = action.payload;
    },
    setShowContactDrawer: (state, action: PayloadAction<boolean>) => {
      state.show_contact_drawer = action.payload;
    },
    setShowTeamingContactDrawer: (state, action: PayloadAction<boolean>) => {
      state.show_teaming_contact_drawer = action.payload;
    },
    setSort: (state, action: PayloadAction<IResourcesSortType>) => {
      state.sort = action.payload;
    },
    setResourcesError: (state, action: PayloadAction<boolean>) => {
      state.resources_error = action.payload;
    },
    setOpenMobileNav: (state, action: PayloadAction<boolean>) => {
      state.open_mobile_nav = action.payload;
    },
    setCurrentFilterSubmenuOpen: (state, action: PayloadAction<IResourcesFilterType | null>) => {
      state.current_filter_submenu_open = action.payload;
    },
    setCurrentBeaconStep: (state, action: PayloadAction<number>) => {
      state.current_beacon_step = action.payload;
    },
    setShowBeacons: (state, action: PayloadAction<boolean>) => {
      state.show_beacons = action.payload;
    },
    setShowOnboardingModal: (state, action: PayloadAction<boolean>) => {
      state.show_onboarding_modal = action.payload;
    },
    setForceScrollSidebar: (state, action: PayloadAction<boolean>) => {
      state.force_scroll_sidebar = action.payload;
    },
    setShouldShowBeacons: (state, action: PayloadAction<boolean>) => {
      state.should_show_beacons = action.payload;
    },
    setSelectedCriteria: (
      state,
      action: PayloadAction<{ [key in DataTypePillType]: string[] }>
    ) => {
      state.selected_criteria = action.payload;
    },
    pushSelectedCriteria: (
      state,
      action: PayloadAction<{ type: DataTypePillType; name: string }>
    ) => {
      if (!state.selected_criteria || !state.selected_criteria[action.payload.type]) {
        state.selected_criteria = {
          ...state.selected_criteria,
          [action.payload.type]: [action.payload.name],
        };
      } else {
        state.selected_criteria[action.payload.type].push(action.payload.name);
      }
    },
    removeSelectedCriteria: (
      state,
      action: PayloadAction<{ type: DataTypePillType; name: string }>
    ) => {
      state.selected_criteria[action.payload.type] = state.selected_criteria[
        action.payload.type
      ].filter((criteria) => criteria !== action.payload.name);

      if (state.selected_criteria[action.payload.type].length === 0) {
        delete state.selected_criteria[action.payload.type];
      }

      if (Object.keys(state.selected_criteria).length === 0) {
        state.selected_criteria = null;
      }
    },
    setForceSkeletonList: (state, action: PayloadAction<boolean>) => {
      state.force_skeleton_list = action.payload;
    },
    setSelectionFilter: (
      state,
      action: PayloadAction<
        {
          [RESOURCES_FILTER_TYPE.GROUPS]?: string[];
        } & {
          [key in Exclude<
            IResourcesFilterType,
            typeof RESOURCES_FILTER_TYPE.GROUPS
          >]?: IResourcesFilter[];
        }
      >
    ) => {
      state.selection_filters = action.payload;
    },
    resetOptionalFilters: (state) => {
      state.optional_filters = {
        [RESOURCES_FILTER_TYPE.CURRENT_COMPANY]: [],
        [RESOURCES_FILTER_TYPE.RESOURCE_TYPES]: [],
        [RESOURCES_FILTER_TYPE.FORMATS]: [],
        [RESOURCES_FILTER_TYPE.CLIENTS]: [],
        [RESOURCES_FILTER_TYPE.INDUSTRIES]: [],
      };
    },
    setOptionalFilter: (
      state,
      action: PayloadAction<{ type: IResourcesFilterType; filters: IResourcesFilter[] }>
    ) => {
      state.optional_filters[action.payload.type] = action.payload.filters;
    },
    setOptionalFilters: (state, action: PayloadAction<{ filters: IResourcesFilters }>) => {
      state.optional_filters = action.payload.filters;
    },
    setAdditionalOptionalFilters: (
      state,
      action: PayloadAction<{ employees: boolean; freelancers: boolean }>
    ) => {
      state.additional_optional_filters = action.payload;
    },
    pushResultsBannerFilter: (state, action: PayloadAction<IResultsBannerFilterType>) => {
      if (state.results_banner_filters.currently_showing.includes(action.payload)) {
        return;
      }

      state.results_banner_filters.currently_showing.push(action.payload);
    },
    removeResultsBannerFilter: (state, action: PayloadAction<IResultsBannerFilterType>) => {
      state.results_banner_filters.currently_showing =
        state.results_banner_filters.currently_showing.filter(
          (filter) => filter !== action.payload
        );
      state.optional_filters[newSelectedCriteriaToOldModels[action.payload]] = [];
    },
    setCurrentStep: (state, action: PayloadAction<number>) => {
      state.current_step = action.payload;
    },
    setSearchBarCurrentTab: (state, action: PayloadAction<string>) => {
      state.search_bar.current_tab = action.payload;
    },
    setNLPResourcesResults: (state, action: PayloadAction<IResourcesNLP[]>) => {
      state.nlp_resources_results = action.payload;
    },
    setScope: (state, action: PayloadAction<{ type: string; uuid: string }>) => {
      state.scope = action.payload;
    },
    setShowBackButton: (state, action: PayloadAction<boolean>) => {
      state.show_back_button = action.payload;
    },
    setCurrentSection: (state, action: PayloadAction<ResourcesSection>) => {
      state.current_section = action.payload;
    },
    setScrollPosition: (state, action: PayloadAction<number>) => {
      state.scroll_position = action.payload;
    },
    setDrafts: (state, action: PayloadAction<IResource[]>) => {
      state.drafts = action.payload;
    },
  },
});

export const {
  setContext,
  setDetails,
  addFilter,
  setExclusiveFilter,
  setExclusiveFilters,
  setFilter,
  removeFromFilter,
  emptyFilter,
  emptyAllFilters,
  updatePreviousFilters,
  setUuids,
  setDefaultUuids,
  setResources,
  setDefaultResources,
  pushResources,
  setSelectedResources,
  pushSelectedResources,
  removeSelectedResources,
  setShouldFetch,
  setShouldGetMoreResources,
  setCurrentPage,
  setIsResourcesLoading,
  setIsMoreResourcesLoading,
  setForceDropdownFilterOpen,
  setCurrentSelectedPerson,
  setShowContactDrawer,
  setShowTeamingContactDrawer,
  setResourcesError,
  setSort,
  setOpenMobileNav,
  setCurrentFilterSubmenuOpen,
  setCurrentBeaconStep,
  setShowBeacons,
  setShowOnboardingModal,
  setFilters,
  setForceScrollSidebar,
  setShouldShowBeacons,
  setSelectedCriteria,
  pushSelectedCriteria,
  removeSelectedCriteria,
  setForceSkeletonList,
  setOriginalResources,
  setSelectionFilter,
  resetOptionalFilters,
  setOptionalFilter,
  setOptionalFilters,
  setAdditionalOptionalFilters,
  pushResultsBannerFilter,
  removeResultsBannerFilter,
  setCurrentStep,
  setSearchBarCurrentTab,
  setNLPResourcesResults,
  setScope,
  setShowBackButton,
  setAccountHolders,
  setCurrentSection,
  setScrollPosition,
  setDrafts,
} = slice.actions;

export const detailsSelector = (state): IResourcesDetails => state.resources.details;
export const filtersSelector = (state): IResourcesFilters => state.resources.filters;
export const exclusiveFiltersSelector = (state): IResourcesExclusiveFilters =>
  state.resources.exclusive_filters;
export const previousFiltersSelector = (state): IResourcesFilters =>
  state.resources.previous_filters;
export const selectedResourcesSelector = (state): IResource[] => state.resources.selected_resources;
export const uuidsSimpleSelector = (state): IResourcesUuids[] => state.resources.uuids;
export const defaultUuidsSelector = (state): IResourcesUuids[] => state.resources.default_uuids;
export const filtersLengthSelector = (state): number => state.resources.filters_length;
export const previousFiltersLengthSelector = (state): number =>
  state.resources.previous_filters_length;
export const shouldFetchSelector = (state): boolean => state.resources.should_fetch;
export const shouldGetMoreResourcesSelector = (state): boolean =>
  state.resources.should_get_more_resources;
export const currentPageSelector = (state): number => state.resources.current_page;
export const isResourcesLoadingSelector = (state): boolean => state.resources.is_resources_loading;
export const isMoreResourcesLoadingSelector = (state): boolean =>
  state.resources.is_more_resources_loading;
export const forceDropdownFilterOpenSelector = (state): boolean =>
  state.resources.force_dropdown_filter_open;
export const currentSelectedPersonSelector = (state): IResource | null =>
  state.resources.current_selected_person;
export const showContactDrawerSelector = (state): boolean => state.resources.show_contact_drawer;
export const showTeamingContactDrawerSelector = (state): boolean =>
  state.resources.show_teaming_contact_drawer;
export const resourcesErrorSelector = (state): boolean => state.resources.resources_error;
export const sortSelector = (state): IResourcesSortType => state.resources.sort;
export const openMobileNavSelector = (state): boolean => state.resources.open_mobile_nav;
export const currentFilterSubmenuOpenSelector = (state): IResourcesFilterType | null =>
  state.resources.current_filter_submenu_open;
export const currentBeaconStepSelector = (state): number => state.resources.current_beacon_step;
export const showBeaconsSelector = (state): boolean => state.resources.show_beacons;
export const shouldShowBeaconsSelector = (state): boolean => state.resources.should_show_beacons;
export const showOnboardingModalSelector = (state): boolean =>
  state.resources.show_onboarding_modal;
export const forceScrollSidebarSelector = (state): boolean => state.resources.force_scroll_sidebar;
export const selectedCriteriaSelector = (
  state
): { [key in DataTypePillType]: { name: string }[] } => state.resources.selected_criteria;
export const forceSkeletonListSelector = (state): boolean => state.resources.force_skeleton_list;
export const optionalFiltersSelector = (
  state
): { [key in IResourcesFilterType]: IResourcesFilter[] | ICollectionDetail[] } =>
  state.resources.optional_filters;
export const additionalOptionalFiltersSelector = (
  state
): { employees: boolean; freelancers: boolean } => state.resources.additional_optional_filters;
export const selectionFiltersSelector = (
  state
): { [key in IResourcesFilterType]: IResourcesFilter[] } => state.resources.selection_filters;
export const resultsBannerFiltersSelector = (state): IResultsBannerFilterType[] =>
  state.resources.results_banner_filters.currently_showing;
export const nlpResourcesResultsSelector = (state): IResourcesNLP[] =>
  state.resources.nlp_resources_results;
export const searchBarCurrentTabSelector = (state): string =>
  state.resources.search_bar.current_tab;
export const currentStepSelector = (state): number => state.resources.current_step;
export const scopeSelector = (state): { type: string; uuid: string } => state.resources.scope;
export const showBackButtonSelector = (state): boolean => state.resources.show_back_button;
export const currentSectionSelector = (state): ResourcesSection => state.resources.current_section;
export const draftsSelector = (state): IResource[] => state.resources.drafts;
export const scrollPositionSelector = (state): number => state.resources.scroll_position;

const selectResources = (state): IResource[] => state.resources.resources;
const selectDefaultResources = (state): IResource[] => state.resources.default_resources;
const selectUuids = (state): IResourcesUuids[] => state.resources.uuids;
const originalResources = (state): IResource[] => state.resources.original_resources;
const selectDefaultUuids = (state): IResourcesUuids[] => state.resources.default_uuids;

export const resourcesSelector = createSelector(
  [selectResources, selectUuids],
  (resources, uuids) => {
    return resources.map((person) => {
      const uuid = uuids.find((u) => u.uuid === person.uuid);
      return {
        ...person,
        matches: uuid ? uuid.groups_matched?.length : 0,
        matched_elements: uuid?.groups_matched,
        matched_detail: uuid?.groups_matched?.map((group) => ({
          name: group,
          type: 'name',
        })),
        groups_matched: uuid?.groups_matched,
      };
    });
  }
);

export const originalResourcesSelector = createSelector(
  [originalResources, selectUuids],
  (resources, uuids) => {
    return resources.map((person) => {
      const uuid = uuids.find((u) => u.uuid === person.uuid);
      return {
        ...person,
        matches: uuid ? uuid.groups_matched?.length : 0,
        matched_elements: uuid?.groups_matched,
        matched_detail: uuid?.groups_matched?.map((group) => ({
          name: group,
          type: 'name',
        })),
        groups_matched: uuid?.groups_matched,
      };
    });
  }
);
export const defaultResourcesSelector = createSelector(
  [selectDefaultResources, selectDefaultUuids],
  (resources, uuids) => {
    return resources.map((person) => {
      const uuid = uuids.find((u) => u.uuid === person.uuid);
      return {
        ...person,
        matches: uuid ? uuid.groups_matched?.length : 0,
        matched_elements: uuid?.groups_matched,
        matched_detail: uuid?.groups_matched?.map((group) => ({
          name: group,
          type: 'name',
        })),
        groups_matched: uuid?.groups_matched,
      };
    });
  }
);

export const uuidsSelector = createSelector(
  [uuidsSimpleSelector, nlpResourcesResultsSelector],
  (uuids: IResourcesUuids[], nlpResourcesResults: IResourcesNLP[]) => {
    const processedUuids = uuids.map((uuid) => {
      const nlpUuid = nlpResourcesResults.find((p) => p.uuid === uuid.uuid);
      if (nlpUuid) {
        const groups_matched = uuid?.groups_matched
          ? [...uuid?.groups_matched, ...nlpUuid?.groups_matched]
          : nlpUuid?.groups_matched;
        const matched_elements = uuid?.groups_matched
          ? [
              ...uuid?.groups_matched,
              ...nlpUuid?.groups_matched?.map((group) => ({
                name: group,
                type: 'name',
              })),
            ]
          : nlpUuid?.groups_matched?.map((group) => ({
              name: group,
              type: 'name',
            }));
        const matched_detail = uuid?.groups_matched?.map((group) => ({
          name: group,
          type: 'name',
        }));

        return {
          ...uuid,
          groups_matched,
          matched_elements,
          matched_detail,
          matches: uuid?.groups_matched?.length + nlpUuid?.groups_matched?.length,
        };
      }

      return uuid;
    });

    const nlpOnlyResults = nlpResourcesResults
      .filter((nlp) => !uuids.some((uuid) => uuid.uuid === nlp.uuid))
      .map((nlp) => ({
        uuid: nlp.uuid,
        name: nlp.name,
        groups_matched: nlp.groups_matched,
        matched_elements: nlp.groups_matched,
        matched_detail: nlp.groups_matched,
        matches: nlp.groups_matched?.length,
        engagement: nlp.engagement,
        level: nlp.level,
        location: nlp.location,
      }));

    return [...processedUuids, ...nlpOnlyResults];
  }
);

export const fullCriteriaUuidsSelector = createSelector(
  [selectUuids, previousFiltersLengthSelector],
  (uuids, previous_filters_length) =>
    uuids.filter((p) => p.groups_matched.length === previous_filters_length).map((p) => p.uuid)
);

export const partialCriteriaUuidsSelector = createSelector(
  [selectUuids, previousFiltersLengthSelector],
  (uuids, previous_filters_length) =>
    uuids.filter((p) => p.groups_matched.length < previous_filters_length).map((p) => p.uuid)
);

export const allCurrentFiltersSelector = createSelector(
  [filtersSelector, exclusiveFiltersSelector],
  (filters, exclusive_filters): IResourcesFilters => {
    const inclusiveFilters = Object.keys(filters).reduce<IResourcesFilters>((acc, key) => {
      if (filters[key].length > 0) {
        return { ...acc, [key]: filters[key].map((f) => f.uuid) };
      }
      return acc;
    }, {});

    const allFilters = Object.keys(exclusive_filters).reduce<IResourcesFilters>((acc, key) => {
      if (exclusive_filters[key]) {
        return { ...acc, [key]: true };
      }
      return acc;
    }, inclusiveFilters);

    return {
      ...allFilters,
    };
  }
);

export const sortedUuidsSelector = createSelector([uuidsSelector, sortSelector], (uuids, sort) => {
  if (uuids.length === 0) return [];

  if (sort === 'relevance') {
    return [...uuids].sort((a, b) => b.groups_matched.length - a.groups_matched.length);
  }

  return uuids;
});

export const filtersNamesSelector = createSelector([filtersSelector], (filters) => {
  return Object.keys(filters).reduce((acc, key) => {
    if (filters[key].length > 0) {
      return [...acc, ...filters[key].map((f) => f.name)];
    }
    return acc;
  }, []);
});

export const allFiltersNamesSelector = createSelector(
  [filtersSelector, exclusiveFiltersSelector],
  (filters, exclusive_filters) => {
    if (!filters && !exclusive_filters) return [];
    let inclusiveFilters = [];

    if (filters) {
      inclusiveFilters = Object.keys(filters).reduce<string[]>((acc, key) => {
        if (filters[key].length > 0) {
          return [...acc, ...filters[key].map((f) => f.name)];
        }
        return acc;
      }, []);
    }

    const exclusiveFilters =
      Object.keys(exclusive_filters)?.reduce<string[]>((acc, key: IResourcesFilterType) => {
        if (exclusive_filters[key]) {
          return [...acc, exclusiveFilterToTitle.get(key)];
        }
        return acc;
      }, []) || [];

    return [...inclusiveFilters, ...exclusiveFilters];
  }
);

export const allPreviousFiltersNamesSelector = createSelector(
  [previousFiltersSelector],
  (filters) => {
    if (
      !filters ||
      Object.keys(filters).length === 0 ||
      Object.keys(filters).every((key) => filters[key].length === 0)
    )
      return {};
    const inclusiveFilters = Object.keys(filters).reduce<string[]>((acc, key) => {
      if (filters[key].length > 0) {
        return [...acc, ...filters[key].map((f) => f.name)];
      }
      return acc;
    }, []);

    return inclusiveFilters;
  }
);

export const currentExclusiveFiltersActiveSelector = createSelector(
  [exclusiveFiltersSelector],
  (filters: IResourcesExclusiveFilters): IResourcesExclusiveFilterType[] => {
    return Object.keys(filters).reduce((acc, filterKey) => {
      if (filters[filterKey]) {
        acc.push(filterKey);
      }

      return acc;
    }, []);
  }
);

export const allOptionalFiltersSelector = createSelector(
  [optionalFiltersSelector],
  (optionalFilters): (IResourcesFilter & { type: IResourcesFilterType })[] => {
    return Object.keys(optionalFilters).reduce((acc, key) => {
      return [...acc, ...optionalFilters[key].map((filter) => ({ type: key, ...filter }))];
    }, []);
  }
);

export const filteredUuidsSelector = createSelector(
  [uuidsSelector, selectionFiltersSelector, optionalFiltersSelector],
  (uuids: IResourcesUuids[], selectionFilters, optionalFilters) => {
    if (
      !selectionFilters &&
      Object.keys(optionalFilters).every((filter) => optionalFilters[filter].length === 0)
    )
      return uuids;

    if (
      selectionFilters !== null &&
      Object.values(selectionFilters).length === 0 &&
      Object.keys(optionalFilters).every((filter) => optionalFilters[filter].length === 0)
    )
      return uuids;

    if (
      selectionFilters !== null &&
      Object.keys(selectionFilters).length === 1 &&
      selectionFilters[RESOURCES_FILTER_TYPE.GROUPS] &&
      (!optionalFilters ||
        Object.values(optionalFilters).every((filter: any[]) => filter.length === 0))
    ) {
      return uuids.filter((uuid) => {
        return selectionFilters[RESOURCES_FILTER_TYPE.GROUPS].every(
          (group: unknown) => uuid.groups_matched && uuid.groups_matched.includes(group as string)
        );
      });
    }

    return uuids
      .filter((uuid) => {
        if (selectionFilters === null) return true;
        return selectionFilters[RESOURCES_FILTER_TYPE.GROUPS].every(
          (group: unknown) => uuid.groups_matched && uuid.groups_matched.includes(group as string)
        );
      })
      .filter((uuid: IResourcesUuids) => {
        if (Object.values(optionalFilters).length === 0) return true;
        let isValid = true;

        const companies = optionalFilters[RESOURCES_FILTER_TYPE.CURRENT_COMPANY];
        const clients = optionalFilters[RESOURCES_FILTER_TYPE.CLIENTS];
        const industries = optionalFilters[RESOURCES_FILTER_TYPE.INDUSTRIES];
        const resource_types = optionalFilters[RESOURCES_FILTER_TYPE.RESOURCE_TYPES];
        const formats = optionalFilters[RESOURCES_FILTER_TYPE.FORMATS];

        if (companies.length > 0) {
          isValid = companies.some((company) => {
            if (company.uuid === NO_COMPANY_COLLECTION_DETAIL.uuid && uuid.companies.length === 0) {
              return true;
            }

            return uuid.companies.some((c) => c.uuid === company.uuid);
          });
        }

        if (!isValid) return isValid;

        if (clients.length > 0) {
          isValid = clients.some((client) => {
            if (client.uuid === NO_CLIENT_COLLECTION_DETAIL.uuid && uuid.clients.length === 0) {
              return true;
            }

            return uuid.clients.some((c) => c.uuid === client.uuid);
          });
        }

        if (!isValid) return isValid;

        if (industries.length > 0) {
          isValid = industries.some((industry) => {
            if (
              industry.uuid === NO_INDUSTRY_COLLECTION_DETAIL.uuid &&
              uuid.industries.length === 0
            ) {
              return true;
            }

            return uuid.industries.some((i) => i.uuid === industry.uuid);
          });
        }

        if (!isValid) return isValid;

        if (resource_types.length > 0) {
          isValid = resource_types.some((resource_type) => uuid.type?.uuid === resource_type.uuid);
        }

        if (!isValid) return isValid;

        if (formats.length > 0) {
          isValid = formats.some((format) => uuid.format?.uuid === format.uuid);
        }

        return isValid;
      });
  }
);

export const selectedCriteriaFiltersSelector = createSelector(
  [selectedCriteriaSelector],
  (selectedCriteria) => {
    if (!selectedCriteria) return [];
    return Object.keys(selectedCriteria).reduce((acc, key) => {
      return [...acc, ...selectedCriteria[key].map((name) => ({ type: key, name }))];
    }, []);
  }
);

export const NLPResourcesGroupsSelector = createSelector(
  [nlpResourcesResultsSelector],
  (nlpResourcesResults) => {
    return nlpResourcesResults
      .map((result) => result.groups_matched[0])
      .filter((group, index, self) => self.indexOf(group) === index);
  }
);

export interface IGroupedFilter {
  label: string;
  type: 'group' | 'single';
  filters: IResourcesFilter[];
  datatype: DataTypePillType;
}

export const groupedFiltersSelector = createSelector(
  [previousFiltersSelector, NLPResourcesGroupsSelector],
  (filters: IResourcesFilters, nlpResourcesGroups: string[]): IGroupedFilter[] => {
    const allFilters = Object.keys(filters).reduce((acc, key) => {
      if (filters[key]?.length > 0) {
        return [...acc, ...filters[key].map((f) => ({ ...f, datatype: key }))];
      }
      return acc;
    }, []);

    const groupedFilters = allFilters.reduce((acc, filter) => {
      const group = filter.grouping;
      if (group) {
        acc[group] = acc[group] && acc[group].length > 0 ? [...acc[group], filter] : [filter];
      }

      return acc;
    }, {});

    const resourcesGroups = nlpResourcesGroups.reduce((acc, group) => {
      acc[group] = acc[group] && acc[group].length > 0 ? [...acc[group], group] : [group];
      return acc;
    }, {});

    return { ...groupedFilters, ...resourcesGroups };
  }
);

export const companiesFiltersSelector = createSelector(
  [uuidsSelector],
  (uuids: IResourcesUuids[]): ICollectionDetail[] => {
    const companies = uuids.reduce((acc, uuid) => {
      if (uuid.companies?.length > 0) {
        uuid.companies.forEach((company) => {
          const included = acc.find((c) => c.uuid === company.uuid);

          if (!included) {
            acc.push(company);
          }
        });
      } else {
        const included = acc.find((c) => c.uuid === NO_COMPANY_COLLECTION_DETAIL.uuid);

        if (!included) {
          acc.push(NO_COMPANY_COLLECTION_DETAIL);
        }
      }
      return acc;
    }, []);

    return companies;
  }
);

export const clientsFiltersSelector = createSelector(
  [uuidsSelector],
  (uuids: IResourcesUuids[]): ICollectionDetail[] => {
    const clients = uuids.reduce((acc, uuid) => {
      if (uuid.clients?.length > 0) {
        uuid.clients.forEach((client) => {
          const included = acc.find((c) => c.uuid === client.uuid);

          if (!included) {
            acc.push(client);
          }
        });
      } else {
        const included = acc.find((c) => c.uuid === NO_CLIENT_COLLECTION_DETAIL.uuid);

        if (!included) {
          acc.push(NO_CLIENT_COLLECTION_DETAIL);
        }
      }
      return acc;
    }, []);

    return clients;
  }
);

export const industriesFiltersSelector = createSelector(
  [uuidsSelector],
  (uuids: IResourcesUuids[]): ICollectionDetail[] => {
    const industries = uuids.reduce((acc, uuid) => {
      if (uuid.industries?.length > 0) {
        uuid.industries.forEach((industry) => {
          const included = acc.find((i) => i.uuid === industry.uuid);

          if (!included) {
            acc.push(industry);
          }
        });
      } else {
        const included = acc.find((i) => i.uuid === NO_INDUSTRY_COLLECTION_DETAIL.uuid);

        if (!included) {
          acc.push(NO_INDUSTRY_COLLECTION_DETAIL);
        }
      }
      return acc;
    }, []);

    return industries;
  }
);

export const typesFiltersSelector = createSelector(
  [uuidsSelector],
  (uuids: IResourcesUuids[]): ICollectionDetail[] => {
    const types = uuids.reduce((acc, uuid) => {
      if (uuid.type) {
        const included = acc.find((type) => type.uuid === uuid.type.uuid);

        if (!included) {
          acc.push(uuid.type);
        }
      }
      return acc;
    }, []);

    return types;
  }
);

export const formatsFiltersSelector = createSelector(
  [uuidsSelector],
  (uuids: IResourcesUuids[]): ICollectionDetail[] => {
    const formats = uuids.reduce((acc, uuid) => {
      if (uuid.format) {
        const included = acc.find((format) => format.uuid === uuid.format.uuid);

        if (!included) {
          acc.push(uuid.format);
        }
      }
      return acc;
    }, []);

    return formats;
  }
);

export const perfectMatchesSelector = createSelector(
  [uuidsSelector, groupedFiltersSelector, nlpResourcesResultsSelector],
  (uuids, groupedFilters, nlpResourcesResults) => {
    if (Object.keys(groupedFilters).length === 0 && nlpResourcesResults.length === 0) {
      return [];
    }

    if (nlpResourcesResults.length > 0 && Object.keys(groupedFilters).length === 0) {
      return nlpResourcesResults;
    }

    return uuids.filter((uuid) => {
      return Object.keys(groupedFilters).every((group) => {
        return uuid.groups_matched?.includes(group);
      });
    });
  }
);

export const currentGroupsAppliedSelector = createSelector(
  [selectionFiltersSelector],
  (selectionFilters) => {
    return (selectionFilters?.[RESOURCES_FILTER_TYPE.GROUPS] as unknown as string[]) || [];
  }
);

export const companiesSelector = createSelector([uuidsSelector], (uuids: IResourcesUuids[]) => {
  return uuids
    .reduce((acc, uuid) => {
      if (uuid.companies?.length > 0) {
        uuid.companies.forEach((company) => {
          const included = acc.find((c) => c.uuid === company.uuid);

          if (!included) {
            acc.push(company);
          }
        });
      }
      return acc;
    }, [])
    .sort((a, b) => a.name.localeCompare(b.name));
});

export const clientsSelector = createSelector([uuidsSelector], (uuids: IResourcesUuids[]) => {
  return uuids
    .reduce((acc, uuid) => {
      if (uuid.clients?.length > 0) {
        uuid.clients.forEach((client) => {
          const included = acc.find((c) => c.uuid === client.uuid);

          if (!included) {
            acc.push(client);
          }
        });
      }
      return acc;
    }, [])
    .sort((a, b) => a.name.localeCompare(b.name));
});

export const industriesSelector = createSelector([uuidsSelector], (uuids: IResourcesUuids[]) => {
  return uuids.reduce((acc, uuid) => {
    if (uuid.industries) {
      uuid.industries.forEach((industry) => {
        const included = acc.find((i) => i.uuid === industry.uuid);

        if (!included) {
          acc.push(industry);
        }
      });
    }
    return acc;
  }, []);
});

export const resourceFormatsSelector = createSelector(
  [uuidsSelector],
  (uuids: IResourcesUuids[]) => {
    return uuids.reduce((acc, uuid) => {
      if (uuid.format) {
        const included = acc.find((format) => format.uuid === uuid.format.uuid);

        if (!included) {
          acc.push(uuid.format);
        }
      }
      return acc;
    }, []);
  }
);

export const resourceTypesSelector = createSelector([uuidsSelector], (uuids: IResourcesUuids[]) => {
  return uuids.reduce((acc, uuid) => {
    if (uuid.type) {
      const included = acc.find((type) => type.uuid === uuid.type.uuid);

      if (!included) {
        acc.push(uuid.type);
      }
    }
    return acc;
  }, []);
});

export const draftUuidsSelector = createSelector([draftsSelector], (drafts: IResource[]) => {
  return new Set(drafts.map((draft) => draft.uuid));
});

export const availableCompaniesSelector = createSelector(
  [defaultUuidsSelector, optionalFiltersSelector],
  (uuids: IResourcesUuids[], optionalFilters) => {
    const { clients, industries, resource_types, formats } = optionalFilters;
    const companiesMap = new Map();

    let filteredUuids = uuids;

    if (clients.length > 0) {
      filteredUuids = filteredUuids.filter((uuid) =>
        clients.some((client) => {
          if (client.uuid === NO_CLIENT_COLLECTION_DETAIL.uuid && uuid.clients.length === 0) {
            return true;
          }

          return uuid.clients.some((c) => c.uuid === client.uuid);
        })
      );
    }

    if (industries.length > 0) {
      filteredUuids = filteredUuids.filter((uuid) =>
        industries.some((industry) => {
          if (
            industry.uuid === NO_INDUSTRY_COLLECTION_DETAIL.uuid &&
            uuid.industries.length === 0
          ) {
            return true;
          }

          return uuid.industries.some((i) => i.uuid === industry.uuid);
        })
      );
    }

    if (resource_types.length > 0) {
      filteredUuids = filteredUuids.filter((uuid) =>
        resource_types.some((resource_type) => uuid.type?.uuid === resource_type.uuid)
      );
    }

    if (formats.length > 0) {
      filteredUuids = filteredUuids.filter((uuid) =>
        formats.some((format) => uuid.format?.uuid === format.uuid)
      );
    }

    filteredUuids?.forEach((uuid) => {
      if (uuid.companies?.length > 0) {
        uuid.companies.forEach((company) => {
          if (!companiesMap.has(company.uuid)) {
            companiesMap.set(company.uuid, company);
          }
        });
      } else {
        const included = companiesMap.has(NO_COMPANY_COLLECTION_DETAIL.uuid);

        if (!included) {
          companiesMap.set(NO_COMPANY_COLLECTION_DETAIL.uuid, NO_COMPANY_COLLECTION_DETAIL);
        }
      }
    });

    return Array.from(companiesMap.values());
  }
);

export const availableClientsSelector = createSelector(
  [defaultUuidsSelector, optionalFiltersSelector],
  (uuids: IResourcesUuids[], optionalFilters) => {
    const { companies, industries, resource_types, formats } = optionalFilters;
    const clientsMap = new Map();

    let filteredUuids = uuids;

    if (companies.length > 0) {
      filteredUuids = filteredUuids.filter((uuid) =>
        companies.some((company) => {
          if (company.uuid === NO_COMPANY_COLLECTION_DETAIL.uuid && uuid.companies.length === 0) {
            return true;
          }

          return uuid.companies.some((c) => c.uuid === company.uuid);
        })
      );
    }
    if (industries.length > 0) {
      filteredUuids = filteredUuids.filter((uuid) =>
        industries.some((industry) => {
          if (
            industry.uuid === NO_INDUSTRY_COLLECTION_DETAIL.uuid &&
            uuid.industries.length === 0
          ) {
            return true;
          }

          return uuid.industries.some((i) => i.uuid === industry.uuid);
        })
      );
    }
    if (resource_types.length > 0) {
      filteredUuids = filteredUuids.filter((uuid) =>
        resource_types.some((resource_type) => uuid.type?.uuid === resource_type.uuid)
      );
    }

    if (formats.length > 0) {
      filteredUuids = filteredUuids.filter((uuid) =>
        formats.some((format) => uuid.format?.uuid === format.uuid)
      );
    }

    filteredUuids?.forEach((uuid) => {
      if (uuid.clients?.length > 0) {
        uuid.clients.forEach((client) => {
          if (!clientsMap.has(client.uuid)) {
            clientsMap.set(client.uuid, client);
          }
        });
      } else {
        const included = clientsMap.has(NO_CLIENT_COLLECTION_DETAIL.uuid);

        if (!included) {
          clientsMap.set(NO_CLIENT_COLLECTION_DETAIL.uuid, NO_CLIENT_COLLECTION_DETAIL);
        }
      }
    });

    return Array.from(clientsMap.values());
  }
);

export const availableIndustriesSelector = createSelector(
  [defaultUuidsSelector, optionalFiltersSelector],
  (uuids: IResourcesUuids[], optionalFilters) => {
    const { companies, clients, resource_types, formats } = optionalFilters;
    const industriesMap = new Map();

    let filteredUuids = uuids;

    if (companies.length > 0) {
      filteredUuids = filteredUuids.filter((uuid) =>
        companies.some((company) => {
          if (company.uuid === NO_COMPANY_COLLECTION_DETAIL.uuid && uuid.companies.length === 0) {
            return true;
          }

          return uuid.companies.some((c) => c.uuid === company.uuid);
        })
      );
    }

    if (clients.length > 0) {
      filteredUuids = filteredUuids.filter((uuid) =>
        clients.some((client) => {
          if (client.uuid === NO_CLIENT_COLLECTION_DETAIL.uuid && uuid.clients.length === 0) {
            return true;
          }

          return uuid.clients.some((c) => c.uuid === client.uuid);
        })
      );
    }

    if (resource_types.length > 0) {
      filteredUuids = filteredUuids.filter((uuid) =>
        resource_types.some((resource_type) => uuid.type?.uuid === resource_type.uuid)
      );
    }

    if (formats.length > 0) {
      filteredUuids = filteredUuids.filter((uuid) =>
        formats.some((format) => uuid.format?.uuid === format.uuid)
      );
    }

    filteredUuids?.forEach((uuid) => {
      if (uuid.industries?.length > 0) {
        uuid.industries.forEach((industry) => {
          if (!industriesMap.has(industry.uuid)) {
            industriesMap.set(industry.uuid, industry);
          }
        });
      } else {
        const included = industriesMap.has(NO_INDUSTRY_COLLECTION_DETAIL.uuid);

        if (!included) {
          industriesMap.set(NO_INDUSTRY_COLLECTION_DETAIL.uuid, NO_INDUSTRY_COLLECTION_DETAIL);
        }
      }
    });

    return Array.from(industriesMap.values());
  }
);

export const availableResourceTypesSelector = createSelector(
  [defaultUuidsSelector, optionalFiltersSelector],
  (uuids: IResourcesUuids[], optionalFilters) => {
    const { companies, industries, formats, clients } = optionalFilters;
    const resourceTypesMap = new Map();

    let filteredUuids = uuids;

    if (companies.length > 0) {
      filteredUuids = filteredUuids.filter((uuid) =>
        companies.some((company) => {
          if (company.uuid === NO_COMPANY_COLLECTION_DETAIL.uuid && uuid.companies.length === 0) {
            return true;
          }

          return uuid.companies.some((c) => c.uuid === company.uuid);
        })
      );
    }

    if (industries.length > 0) {
      filteredUuids = filteredUuids.filter((uuid) =>
        industries.some((industry) => {
          if (
            industry.uuid === NO_INDUSTRY_COLLECTION_DETAIL.uuid &&
            uuid.industries.length === 0
          ) {
            return true;
          }

          return uuid.industries.some((i) => i.uuid === industry.uuid);
        })
      );
    }

    if (clients.length > 0) {
      filteredUuids = filteredUuids.filter((uuid) =>
        clients.some((client) => {
          if (client.uuid === NO_CLIENT_COLLECTION_DETAIL.uuid && uuid.clients.length === 0) {
            return true;
          }

          return uuid.clients.some((c) => c.uuid === client.uuid);
        })
      );
    }

    if (formats.length > 0) {
      filteredUuids = filteredUuids.filter((uuid) =>
        formats.some((format) => uuid.format?.uuid === format.uuid)
      );
    }

    filteredUuids?.forEach((uuid) => {
      if (uuid.type) {
        if (!resourceTypesMap.has(uuid.type.uuid)) {
          resourceTypesMap.set(uuid.type.uuid, uuid.type);
        }
      }
    });

    return Array.from(resourceTypesMap.values());
  }
);

export const availableFormatsSelector = createSelector(
  [defaultUuidsSelector, optionalFiltersSelector],
  (uuids: IResourcesUuids[], optionalFilters) => {
    const { companies, industries, resource_types, clients } = optionalFilters;
    const formatsMap = new Map();

    let filteredUuids = uuids;

    if (companies.length > 0) {
      filteredUuids = filteredUuids.filter((uuid) =>
        companies.some((company) => {
          if (company.uuid === NO_COMPANY_COLLECTION_DETAIL.uuid && uuid.companies.length === 0) {
            return true;
          }

          return uuid.companies.some((c) => c.uuid === company.uuid);
        })
      );
    }

    if (industries.length > 0) {
      filteredUuids = filteredUuids.filter((uuid) =>
        industries.some((industry) => {
          if (
            industry.uuid === NO_INDUSTRY_COLLECTION_DETAIL.uuid &&
            uuid.industries.length === 0
          ) {
            return true;
          }

          return uuid.industries.some((i) => i.uuid === industry.uuid);
        })
      );
    }

    if (resource_types.length > 0) {
      filteredUuids = filteredUuids.filter((uuid) =>
        resource_types.some((resource_type) => uuid.type?.uuid === resource_type.uuid)
      );
    }

    if (clients.length > 0) {
      filteredUuids = filteredUuids.filter((uuid) =>
        clients.some((client) => {
          if (client.uuid === NO_CLIENT_COLLECTION_DETAIL.uuid && uuid.clients.length === 0) {
            return true;
          }

          return uuid.clients.some((c) => c.uuid === client.uuid);
        })
      );
    }

    filteredUuids?.forEach((uuid) => {
      if (uuid.format) {
        if (!formatsMap.has(uuid.format.uuid)) {
          formatsMap.set(uuid.format.uuid, uuid.format);
        }
      }
    });

    return Array.from(formatsMap.values());
  }
);

export const availableResourceTypesFiltersSelector = createSelector(
  [defaultUuidsSelector, optionalFiltersSelector],
  (uuids: IResourcesUuids[], optionalFilters) => {
    const { companies, industries, formats, clients } = optionalFilters;
    const resourceTypesMap = new Map();

    let filteredUuids = uuids;

    if (companies.length > 0) {
      filteredUuids = filteredUuids.filter((uuid) =>
        companies.some((company) => {
          if (company.uuid === NO_COMPANY_COLLECTION_DETAIL.uuid && uuid.companies.length === 0) {
            return true;
          }

          return uuid.companies.some((c) => c.uuid === company.uuid);
        })
      );
    }
    if (industries.length > 0) {
      filteredUuids = filteredUuids.filter((uuid) =>
        industries.some((industry) => {
          if (
            industry.uuid === NO_INDUSTRY_COLLECTION_DETAIL.uuid &&
            uuid.industries.length === 0
          ) {
            return true;
          }

          return uuid.industries.some((i) => i.uuid === industry.uuid);
        })
      );
    }
    if (clients.length > 0) {
      filteredUuids = filteredUuids.filter((uuid) =>
        clients.some((client) => {
          if (client.uuid === NO_CLIENT_COLLECTION_DETAIL.uuid && uuid.clients.length === 0) {
            return true;
          }

          return uuid.clients.some((c) => c.uuid === client.uuid);
        })
      );
    }

    if (formats.length > 0) {
      filteredUuids = filteredUuids.filter((uuid) =>
        formats.some((format) => uuid.format?.uuid === format.uuid)
      );
    }

    filteredUuids?.forEach((uuid) => {
      if (uuid.type) {
        if (!resourceTypesMap.has(uuid.type.uuid)) {
          resourceTypesMap.set(uuid.type.uuid, uuid.type);
        }
      }
    });

    return Array.from(resourceTypesMap.values());
  }
);

export default slice.reducer;
